import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { Table, type TableColumn, Dropdown, StatusChip } from '../../Common';
import { DiscountDisplay } from '../../../services';
import './DiscountsTable.css';

export interface DiscountsTableProps {
  promoCodes: DiscountDisplay[];
  loading?: boolean;
  onEdit?: (promoCode: DiscountDisplay) => void;
  onArchive?: (promoCodeId: string) => void;
  isArchivedTab?: boolean;
}

const DiscountsTable: React.FC<DiscountsTableProps> = ({
  promoCodes,
  loading = false,
  onEdit,
  onArchive,
  isArchivedTab = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPromoCode, setSelectedPromoCode] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, promocodeId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedPromoCode(promocodeId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPromoCode(null);
  };

  const handleEdit = () => {
    if (selectedPromoCode && onEdit) {
      const promoCode = promoCodes.find(p => p.id === selectedPromoCode);
      if (promoCode) {
        onEdit(promoCode);
      }
    }
    handleMenuClose();
  };

  const handleArchive = () => {
    if (selectedPromoCode && onArchive) {
      onArchive(selectedPromoCode);
    }
    handleMenuClose();
  };

  const columns: TableColumn<DiscountDisplay>[] = [
    {
      key: 'code',
      label: 'Promocode',
      width: '180px',
      render: (promo) => (
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
          {promo.code}
        </Typography>
      ),
    },
    {
      key: 'discount',
      label: 'Discount',
      width: '100px',
      render: (promo) => (
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
          {promo.discount}
        </Typography>
      ),
    },
    {
      key: 'categories',
      label: 'Categories',
      width: 1,
      render: (promo) => (
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
          {promo.categories}
        </Typography>
      ),
    },
    {
      key: 'vendors',
      label: 'Vendors',
      width: 1,
      render: (promo) => (
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
          {promo.vendors}
        </Typography>
      ),
    },
    {
      key: 'expires',
      label: 'Expires',
      width: '120px',
      render: (promo) => (
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
          {promo.expires}
        </Typography>
      ),
    },
    {
      key: 'uses',
      label: 'Uses',
      width: '40px',
      align: 'center',
      render: (promo) => (
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
          {promo.uses}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: '',
      width: '120px',
      render: (promo) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingLeft: '16px' }}>
          <StatusChip status={promo.status as any} />
        </Box>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '56px',
      align: 'center',
      render: (promo) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, promo.id)}
          sx={{
            width: '56px',
            height: '56px',
            borderRadius: 'var(--palette-light-4-value)',
            color: 'var(--palette-light-brand-primary-value-hex)',
            '&:hover': {
              backgroundColor: 'rgba(103, 49, 79, 0.04)',
            }
          }}
        >
          <MoreHorizIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box className="discounts-table-wrapper">
      <Table<DiscountDisplay>
        columns={columns}
        data={promoCodes}
        keyExtractor={(promo) => promo.id}
        loading={loading}
        emptyMessage="No promo codes found"
      />

      {/* Action Menu */}
      <Dropdown
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        minWidth={120}
        options={[
          {
            label: 'Edit',
            value: 'edit',
            onClick: handleEdit,
          },
          {
            label: 'Archive',
            value: 'archive',
            color: 'danger',
            onClick: handleArchive,
            disabled: isArchivedTab,
          },
        ]}
      />
    </Box>
  );
};

export default DiscountsTable;

